"use client";

import { useQuery } from "@tanstack/react-query";
import { Clock, FileText, Loader2, Lock } from "lucide-react";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { ResponsiveModal } from "@/components/responsive-modal";
import { Button } from "@/components/ui/button";
import usePricingModal, { PAYWALL_TYPES } from "@/lib/hooks/use-pricing-modal";
import { cn } from "@/lib/utils";
import { useScopedI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";
import { useCreatePracticeTest } from "../api/use-create-practice-test";
import {
  sourceBasePath,
  sourceInput,
  type TestSource,
} from "../hooks/test-source";

const COUNT_OPTIONS = [10, 20, 30] as const;
const DURATION_FACTORS = [1, 1.5, 2] as const;
const SECONDS_PER_QUESTION = 60;

type CountOption = (typeof COUNT_OPTIONS)[number];
type DurationFactor = (typeof DURATION_FACTORS)[number];

const minutesFor = (count: number, factor: number) =>
  Math.max(5, Math.round((count * SECONDS_PER_QUESTION * factor) / 60 / 5) * 5);

export function CreatePracticeTestModal({
  source,
  open,
  onOpenChange,
}: {
  source: TestSource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const t = useScopedI18n("practiceTest");
  const router = useRouter();
  const posthog = usePostHog();
  const trpc = useTRPC();
  const { onOpen: openPricingModal } = usePricingModal();
  const [questionCount, setQuestionCount] = useState<CountOption>(10);
  const [durationFactor, setDurationFactor] = useState<DurationFactor>(1);
  const create = useCreatePracticeTest();
  const { data: plan } = useQuery(trpc.users.getPlanLimit.queryOptions());
  const hasPremiumAccess = !!plan?.hasPremiumAccess;

  const durationMinutes = minutesFor(questionCount, durationFactor);

  const pickCount = (count: CountOption) => {
    if (!hasPremiumAccess && count > 10) {
      openPricingModal({
        upgradeReason: "Practice test length limit reached",
        paywallType: PAYWALL_TYPES.PRACTICE_TEST_LENGTH,
      });
      return;
    }
    setQuestionCount(count);
  };

  const onCreate = () => {
    create.mutate(
      {
        ...sourceInput(source),
        questionCount,
        durationSeconds: durationMinutes * 60,
      },
      {
        onSuccess: ({ id }) => {
          posthog?.capture("practice_test_created", {
            questionCount,
            durationMinutes,
            sourceKind: source.kind,
          });
          onOpenChange(false);
          router.push(`${sourceBasePath(source)}/test/${id}`);
        },
      },
    );
  };

  const optionClass = (selected: boolean) =>
    cn(
      "flex-1 rounded-lg border py-2 text-sm font-medium transition-all duration-200",
      selected
        ? "border-brand-primary bg-brand-primary/5 text-brand-primary ring-2 ring-brand-primary/20"
        : "border-border bg-muted/40 hover:bg-muted/80",
    );

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={t("setup.title")}
      description={t("setup.subtitle")}
      form={
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium flex items-center gap-1.5">
              <FileText className="size-4" />
              {t("setup.questionCount")}
            </span>
            <div className="flex gap-2">
              {COUNT_OPTIONS.map((count) => {
                const locked = !hasPremiumAccess && count > 10;
                return (
                  <button
                    key={count}
                    type="button"
                    onClick={() => pickCount(count)}
                    className={cn(
                      optionClass(questionCount === count),
                      locked && "text-muted-foreground",
                    )}
                  >
                    <span className="flex items-center justify-center gap-1.5">
                      {locked && <Lock className="size-3.5" />}
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium flex items-center gap-1.5">
              <Clock className="size-4" />
              {t("setup.duration")}
            </span>
            <div className="flex gap-2">
              {DURATION_FACTORS.map((factor) => (
                <button
                  key={factor}
                  type="button"
                  onClick={() => setDurationFactor(factor)}
                  className={optionClass(durationFactor === factor)}
                >
                  {/* @ts-expect-error */}
                  {t("setup.minutes", {
                    count: minutesFor(questionCount, factor),
                  })}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end w-full">
            <Button
              className="w-full md:w-auto"
              onClick={onCreate}
              disabled={create.isPending}
              data-attr="practice-test-create"
            >
              {create.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {create.isPending ? t("setup.creating") : t("setup.create")}
            </Button>
          </div>
        </div>
      }
    />
  );
}
